// public/v3/src/lib/api.js — fetch wrapper with CSRF, auth, errors.
//
// All requests go to relative paths and include credentials so the CCM
// cookie auth works. POST/PUT/DELETE require the CSRF token from the
// ccm_csrf cookie (issued by csrfIssue middleware).

function getCookie(name) {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

// What a 401 should DO depends on which page is asking, so the behaviour is a
// swappable handler rather than a hard-coded navigation. The default is the
// SPA's: reload at / so the in-app LoginPage renders. Avoid a full URL change
// so bookmarks (and the deep-link the user was on) stay stable for the next
// paint. A standalone page — Zephyr, the front-office site — must NOT be
// thrown into the clinical SPA when its 4-hour cookie lapses; it installs its
// own handler and re-renders its own sign-in in place.
function defaultUnauthenticatedHandler() {
  if (window.location.pathname !== '/') {
    window.location.href = '/';
  } else {
    // Already at root — reloading triggers the auth check + login render.
    window.location.reload();
  }
}

let _onUnauthenticated = defaultUnauthenticatedHandler;

// Install a page-specific 401 response. Anything that isn't a function (null)
// restores the default, so a page can undo its own override.
export function setUnauthenticatedHandler(fn) {
  _onUnauthenticated = typeof fn === 'function' ? fn : defaultUnauthenticatedHandler;
}

async function request(method, url, body) {
  const headers = { 'Accept': 'application/json' };
  // R-CF-7 / O121: the web DECLARES which protocol it speaks, on every request.
  // The server reads it in one place at the route edge and answers a positional
  // "1" / "yes" / "skip" / "undo" from a declaring client with a sentence naming
  // the card's control instead of parsing it — this page answers questions by
  // naming them now (ccmApi.orderChatPick / orderSend / orderChatCancel).
  //
  // It is a PROTOCOL declaration, not a build stamp: `X-App-Version` names which
  // build a native client is, which is not what has to be told apart here (an old
  // web bundle and a new one are wire-identical without this header).
  headers['X-CCM-Adapter'] = 'web';
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') {
    const csrf = getCookie('ccm_csrf');
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  const res = await fetch(url, {
    method, credentials: 'include', headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 401) {
    // A handler that throws must not mask the 401 from the caller — every
    // await site downstream is written against this error, not that one.
    try { _onUnauthenticated(); } catch (_) { /* handled below */ }
    throw new Error('unauthenticated');
  }
  if (res.status === 403) {
    const data = await res.json().catch(() => ({}));
    const err = new Error((data && data.error) || 'You do not have access to this feature.');
    err.status = 403;
    err.permissionDenied = !!(data && data.permissionDenied);
    err.body = data;
    throw err; // surfaced to caller; session intact (NO reload)
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Server error shape varies: some routes return `{error: "string"}`,
    // others (e.g. /ai) return `{error: {message: "string"}}`. Coerce both
    // to a string so callers can concatenate `err.message` into toasts
    // without rendering "[object Object]".
    const errBody = data && data.error;
    const message = typeof errBody === 'string'
      ? errBody
      : (errBody && typeof errBody === 'object' && typeof errBody.message === 'string')
        ? errBody.message
        : `HTTP ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

export const api = {
  get:    (url)         => request('GET', url),
  post:   (url, body)   => request('POST', url, body),
  put:    (url, body)   => request('PUT', url, body),
  patch:  (url, body)   => request('PATCH', url, body),
  delete: (url)         => request('DELETE', url),
};

// Convenience wrappers — keep all endpoint paths in one file so renames
// are a single search-and-replace.
//
// Note: there's no pushActivityLog wrapper here. The /data/elation/push-activity-log
// endpoint takes a single (patientId, activityIndex) and pushes one entry.
// The v3 monthly-push flow lives in lib/push-month.js, which builds a synthetic
// consolidated entry, persists it, then pushes that single index — mirroring the
// legacy SPA's pushMonthlyActivitiesAlongsideBillingNote pattern.
//
// Care plan endpoints: GET /data/careplans returns a nested object
// `{ care_plan: { pid: { month: text } }, billing_notes: { pid: { month: { bn_code: text } } } }`
// (NOT an array). Save is POST /data/careplans/:patientId/:month with body
// `{ plan_text, type? }`. The wrappers below match this server shape.
export const ccmApi = {
  // Patients
  listPatients:        ()                          => api.get('/data/patients'),
  getPatient:          (id)                         => api.get(`/data/patients/${id}`),
  updatePatient:       (id, patch)                 => api.put(`/data/patients/${id}`, patch),
  // bulkUpdatePatients(ids, fields, expectedUpdatedAtById?)
  // — expectedUpdatedAtById is an optional map { [patientUuid]: ISO timestamp }
  //   that triggers per-patient optimistic-lock checks on the server. Patients
  //   omitted from the map skip the check (backward compat for legacy callers).
  //   Response: { results: [{ id, ok, updatedAt? } | { id, ok:false, conflict:true, serverUpdatedAt }
  //                          | { id, ok:false, error }] }
  bulkUpdatePatients:  (patientIds, fields, expectedUpdatedAtById) =>
    api.post('/data/patients/bulk-update',
      expectedUpdatedAtById
        ? { patientIds, fields, expectedUpdatedAtById }
        : { patientIds, fields }),

  // Care plans / billing notes (server stores both in care_plans table; type column distinguishes)
  listCarePlans:       ()                          => api.get('/data/careplans'),
  listBillingNotes:    ()                          => api.get('/data/careplans?type=billing_notes'),
  saveCarePlan:        (patientId, month, planText, type) =>
    api.post(`/data/careplans/${encodeURIComponent(patientId)}/${month}`,
             type ? { plan_text: planText, type } : { plan_text: planText }),
  deleteCarePlan:      (patientId, month, type)    =>
    api.delete(`/data/careplans/${encodeURIComponent(patientId)}/${month}${type ? `?type=${encodeURIComponent(type)}` : ''}`),
  saveDocumentEditTelemetry: (patientId, month, body) =>
    api.post(`/data/careplans/${encodeURIComponent(patientId)}/${month}/edit-telemetry`, body || {}),
  listDocumentEditTelemetry: (limit) =>
    api.get('/data/document-edit-telemetry' + (limit ? `?limit=${encodeURIComponent(limit)}` : '')),

  // AI proxy — body shape: { model, messages, max_tokens?, temperature?, system?, systemCache? }.
  // Replaces the broken generateCarePlan/generateBillingNote wrappers (which sent
  // an unsupported `_route` field). Callers build the messages array themselves.
  aiComplete:          (body)                      => api.post('/ai', body),
  reviseAI:            (section, content, instr, opts) =>
    api.post('/ai/revise', { section, content, instruction: instr, ...(opts || {}) }),

  // Elation extras
  firstVisitDates:     (elationIds)                => api.post('/data/elation/first-visit-dates', { elationIds }),
  latestVisitNotes:    (elationIds)                => api.post('/data/elation/latest-visit-notes', { elationIds }),
  patientProviderTeams:(elationIds)                => api.post('/data/elation/patient-provider-teams', { elationIds }),
  phoneNotesInMonth:   (elationIds, month)         => api.post('/data/elation/phone-notes-in-month', { elationIds, month }),
  classifyNotes:       (model, notes)              => api.post('/ai/classify-notes', { model, notes }),
  pushCarePlan:        (body)                      => api.post('/data/elation/push-careplan', body),
  pushCqmScreening:    ({ elationId, month, physicianId }) =>
    api.post('/data/elation/push-cqm-screening', { elationId, month, physicianId }),
  bulkSignVisitNotes:  (body)                      => api.post('/data/elation/bulk-sign', body),
  elationSyncStatus:   ()                          => api.get('/data/elation/sync/status'),

  // Billing state
  getBilledState:      ()                          => api.get('/data/billed-state'),
  saveBilledState:     (body)                      => api.post('/data/billed-state', body),

  // Reference data (read-only from v3; admin writes happen on /v2)
  listProviders:       ()                          => api.get('/data/providers'),
  listStaff:           ()                          => api.get('/data/staff'),
  getSettings:         ()                          => api.get('/data/settings'),
  saveSettings:        (body)                      => api.post('/data/settings', body),

  // Admin: per-tenant user management (server: server/routes/admin.js, admin-gated).
  // createUser body for SSO: { email, displayName, role, authProvider: 'google' }.
  listUsers:           ()                          => api.get('/admin/users'),
  createUser:          (body)                      => api.post('/admin/users', body),
  updateUser:          (id, patch)                 => api.put(`/admin/users/${id}`, patch),
  listElationPhysicians: ()                        => api.get('/admin/elation-physicians'),
  setUserActive:       (id, active)                => api.put(`/admin/users/${id}`, { active }),
  forceLogoutUser:     (id)                        => api.post(`/admin/users/${id}/force-logout`),
  userActivity:        (id)                        => api.get(`/admin/users/${id}/activity`),

  // Admin: custom roles (Phase 2a). Dormant — defining/assigning roles has no
  // enforcement effect until Phase 2b.
  listRoles:           ()                          => api.get('/admin/roles'),
  createRole:          (body)                      => api.post('/admin/roles', body),
  updateRole:          (id, patch)                 => api.put(`/admin/roles/${id}`, patch),
  deleteRole:          (id)                        => api.delete(`/admin/roles/${id}`),

  // Audit — /data/audit accepts arbitrary client-initiated entries
  // ({ action, entity, entityId, detail }); /data/audit-event is allowlist-restricted
  // to a specific set of structured events on the server.
  auditEvent:          (action, entity, entityId, detail) =>
    api.post('/data/audit', { action, entity, entityId, detail }),

  // BHI assessment kiosk — clinician marks a PHQ-9 item-9 at-risk assessment
  // reviewed (clears the at-risk nav badge / Studio chip). ts = the assessment's
  // Unix-ms timestamp from data.bhi.assessments[].ts.
  reviewAssessment:    (patientId, ts)             => api.post('/data/assessment-review', { patientId, ts }),

  // Panel Review (Claude AI batch) — spec 2026-05-12-onboard-rescope §4 step 3.
  // POST /ai requires { model, max_tokens, system, messages } and returns
  // { content: [{ text }] }. The `aiCall` wrapper hands the raw text back to
  // the caller, which parses via parsePanelReviewResponse from lib/panel-review.
  aiCall: ({ system, userPrompt, model = 'bedrock-sonnet', maxTokens = 8192 }) =>
    api.post('/ai', {
      model,
      max_tokens: maxTokens,
      system,
      messages: [{ role: 'user', content: userPrompt }],
    }).then((d) => {
      if (!d || !d.content || !d.content[0] || typeof d.content[0].text !== 'string') {
        throw new Error('AI response missing expected text content');
      }
      return d.content[0].text;
    }),

  // SMS Bot admin — activity log and session detail
  listSmsActivity: () => api.get('/api/sms/activity'),
  getSmsSession: (id) => api.get(`/api/sms/sessions/${id}`),
  runSmsDemo: (message) => api.post('/api/sms/demo', { message }),

  // In-webapp Order Chat (first-class — open to providers/staff)
  // sessionId (optional): explicitly continue THAT conversation — the resumed-
  // thread pattern the native app uses. Without it the server falls back to
  // "newest active session", which is wrong once a user juggles two threads.
  // openedSessionIds (optional): the batch cards this provider has opened, sent
  // only while a batch is on screen (O78). A review HINT that holds a typed
  // "confirm all" to the same gate the button obeys — never an authorisation
  // input, and absent on every single-patient turn.
  sendOrderChat: (message, pinnedPatient, sessionId, openedSessionIds) =>
    api.post('/api/sms/chat/send', {
      message,
      ...(pinnedPatient ? { pinnedPatient } : {}),
      ...(sessionId ? { sessionId } : {}),
      ...(Array.isArray(openedSessionIds) ? { openedSessionIds } : {}),
    }),
  orderEdit: (body) => api.post('/api/sms/chat/order-edit', body),
  questPreview: (sessionId, orderIndex) =>
    api.post('/api/sms/chat/quest-preview', { sessionId, orderIndex }),
  questSend: (sessionId, orderIndex, mode) =>
    api.post('/api/sms/chat/quest-send', { sessionId, orderIndex, mode }),
  genericPreview: (sessionId, orderIndex) =>
    api.post('/api/sms/chat/generic-preview', { sessionId, orderIndex }),
  getPrintVendors: () => api.get('/api/sms/chat/print-vendors'),
  genericSend: (sessionId, orderIndex, mode, faxOverride) =>
    api.post('/api/sms/chat/generic-send', faxOverride ? { sessionId, orderIndex, mode, faxOverride } : { sessionId, orderIndex, mode }),
  // The card's Confirm (O120). `orderIndex: null` confirms the WHOLE session —
  // the chat "yes" this page no longer sends. `expect.version` is the view the
  // button was clicked on: a card a second reader changed underneath is refused
  // (409 `view_stale`) with the refreshed card, and nothing is filed. Required
  // from this client, which always has a version in hand.
  orderSend: (sessionId, orderIndex, mode, expect) =>
    api.post('/api/sms/chat/order-send', {
      sessionId, orderIndex: orderIndex === undefined ? null : orderIndex,
      ...(mode ? { mode } : {}), ...(expect ? { expect } : {}),
    }),
  // R-CF-7: answer the open question by NAMING it — which conversation, which
  // question, which option. The ids are the server's own (`view.question`); this
  // client echoes them and mints nothing.
  orderChatPick: ({ sessionId, questionId, optionId, skip, expect }) =>
    api.post('/api/sms/chat/pick', {
      sessionId, questionId,
      ...(skip ? { skip: true } : { optionId }),
      ...(expect ? { expect } : {}),
    }),
  orderChatCancel: (sessionId, expect) =>
    api.post('/api/sms/chat/cancel', { sessionId, ...(expect ? { expect } : {}) }),
  orderChatUndo: (sessionId) => api.post('/api/sms/chat/undo', { sessionId }),
  // Confirm every reviewed card of a multi-patient batch (O64). N ordinary
  // per-card confirms, walked in roster order — never a transmit.
  // sessionId (optional): the page's pin, so the reply's chips answer the
  // session the page's NEXT message will reach (it comes back named as
  // `quickRepliesFor`). Absent = unpinned, the engine's active session.
  batchConfirm: (batchId, sessionId) =>
    api.post('/api/sms/chat/batch-confirm', { batchId, ...(sessionId ? { sessionId } : {}) }),
  searchOrderChatPatients: (q) =>
    api.get('/api/sms/chat/patients?q=' + encodeURIComponent(q)),
  submitQuestHomeboundOrder: (payload) => api.post('/api/quest-homebound/order', payload),
  // Quest homebound standalone form — side-effect-free requisition preview
  // (the preview-before-send gate) + per-test ICD-10 auto-resolve (404 when
  // the practice's auto-dx flag is off → the form falls back to manual).
  previewQuestHomeboundOrder: (payload) => api.post('/api/quest-homebound/preview', payload),
  resolveQuestHomeboundDx: (elationPatientId, names) =>
    api.post('/api/quest-homebound/resolve-dx', { elationPatientId, tests: names.map((name) => ({ name })) }),

  // Provider letters (server: routes/providerLetters.js — provider/admin +
  // orderchat permission; per-practice providerLettersEnabled dormancy → 404).
  // Templates are the builder section; draft/preview/send is the outbound flow.
  listLetterTemplates: (includeRetired) =>
    api.get('/api/provider-letters/templates' + (includeRetired ? '?includeRetired=true' : '')),
  createLetterTemplate: (body) => api.post('/api/provider-letters/templates', body),
  updateLetterTemplate: (id, body) => api.put(`/api/provider-letters/templates/${encodeURIComponent(id)}`, body),
  setLetterTemplateStatus: (id, status) =>
    api.post(`/api/provider-letters/templates/${encodeURIComponent(id)}/status`, { status }),
  deleteLetterTemplate: (id) => api.delete(`/api/provider-letters/templates/${encodeURIComponent(id)}`),
  draftProviderLetter: (body) => api.post('/api/provider-letters/draft', body),
  previewProviderLetter: (body) => api.post('/api/provider-letters/preview', body),
  sendProviderLetter: (body) => api.post('/api/provider-letters/send', body),

  // Patient self-scheduling — the provider Scheduling tab (server:
  // routes/scheduling.js — requireRole('admin','provider') +
  // requirePermission('scheduling'); no dormancy 404, the page works before
  // the selfSchedulingEnabled flag is on). GET/PUT carry the whole
  // selfSchedulingConfig document with an optimistic `version` (PUT → 409
  // code 'version_conflict'); preview never persists and returns counts only.
  getSchedulingConfig: () => api.get('/api/scheduling/config'),
  saveSchedulingConfig: (config) => api.put('/api/scheduling/config', { config }),
  previewSchedulingSlots: (body) => api.post('/api/scheduling/preview', body || {}),
  listSchedulingPhysicians: () => api.get('/api/scheduling/physicians'),
  listSchedulingAppointmentTypes: () => api.get('/api/scheduling/appointment-types'),
  // Google Calendar → Elation sync (routes/calendarSync.js, same gate as the
  // tab). PUT carries the whole googleCalendarSync document with its `version`
  // (409 code:'version_conflict'); /events lists recent decisions (PHI, audited).
  getCalendarSyncConfig: () => api.get('/api/scheduling/calendar-sync/config'),
  saveCalendarSyncConfig: (config) => api.put('/api/scheduling/calendar-sync/config', { config }),
  listCalendarSyncEvents: () => api.get('/api/scheduling/calendar-sync/events?limit=50'),

  // Lab Orders page (server: routes/labOrders.js — provider/admin +
  // orderchat permission, same gates as /api/quest-homebound).
  getLabVendors: () => api.get('/api/lab-orders/vendors'),
  createLabEReq: (body) => api.post('/api/lab-orders/ereq', body),
  labPrintPreview: (body) => api.post('/api/lab-orders/print/preview', body),
  labPrintSend: (body) => api.post('/api/lab-orders/print/send', body),
  // GET /sets is fail-soft: {sets:[]} WITHOUT ok means unavailable (chips row
  // hides); ok:true marks a trustworthy (possibly empty) list.
  listLabOrderSets: () => api.get('/api/lab-orders/sets'),
  createLabOrderSet: (body) => api.post('/api/lab-orders/sets', body),
  renameLabOrderSet: (id, name) => api.patch(`/api/lab-orders/sets/${encodeURIComponent(id)}`, { name }),
  deleteLabOrderSet: (id) => api.delete(`/api/lab-orders/sets/${encodeURIComponent(id)}`),
  listOrderChatSessions: () => api.get('/api/sms/chat/sessions'),
  orderChatSessionCard: (id) => api.get(`/api/sms/chat/sessions/${encodeURIComponent(id)}/card`),
  getOrderChatWhoami: () => api.get('/api/sms/chat/whoami'),
  // GPT-Live Talk mode (server/routes/live.js). /config is 404 while the practice's
  // liveAssistantEnabled is OFF — the page shows no Talk button. /token trades the
  // httpOnly cookie for the socket-scoped token the control socket's `hello` takes
  // (lib/live-client.js); it is never stored, only handed to the socket.
  getLiveConfig: () => api.get('/api/live/config'),
  getLiveToken:  () => api.get('/api/live/token'),
  updateOrderChatSettings: (settings) => api.patch('/api/sms/chat/settings', settings),
  // Group-home refill sheets (server: routes/orderChatRoster.js — Order Chat's own
  // gate; every route 404s while the practice's orderChatRosterEnabled is off).
  // The upload body is the shared page validator's: `{ facilityId, images:
  // [{ mimeType, base64 }] }` or `{ facilityId, pdfBase64 }` → 202 { rosterId }.
  // A page image is an <img src> (cookie auth, GET): rosterSheetPageUrl.
  listRosterFacilities: () => api.get('/api/sms/chat/roster/facilities'),
  listRecentRosterSheets: () => api.get('/api/sms/chat/roster'),
  uploadRosterSheet: (body) => api.post('/api/sms/chat/roster', body),
  getRosterSheet: (rosterId) => api.get(`/api/sms/chat/roster/${encodeURIComponent(rosterId)}`),
  discardRosterSheet: (rosterId) => api.post(`/api/sms/chat/roster/${encodeURIComponent(rosterId)}/discard`, {}),
  searchRosterLine: (rosterId, line) =>
    api.post(`/api/sms/chat/roster/${encodeURIComponent(rosterId)}/lines/${encodeURIComponent(line)}/search`, {}),
  pickRosterLinePatient: (rosterId, line, patientId) =>
    api.post(`/api/sms/chat/roster/${encodeURIComponent(rosterId)}/lines/${encodeURIComponent(line)}/patient`, { patientId }),
  // #747 r2 D4: decide a not-drafted line again (the chart was unreachable, or the
  // staging failed), and "draft anyway" for a duplicate — a card the provider confirms.
  retryRosterLine: (rosterId, line) =>
    api.post(`/api/sms/chat/roster/${encodeURIComponent(rosterId)}/lines/${encodeURIComponent(line)}/retry`, {}),
  draftRosterLineAnyway: (rosterId, line) =>
    api.post(`/api/sms/chat/roster/${encodeURIComponent(rosterId)}/lines/${encodeURIComponent(line)}/card`, {}),
  getSmsProviderMapping: () => api.get('/api/sms/provider-mapping'),
  setSmsProviderMapping: (userId, elationUserId) =>
    api.post('/api/sms/provider-mapping', { userId, elationUserId }),
  syncSmsPatientDirectory: () => api.post('/api/sms/sync-directory'),
  syncSmsLabCatalog: () => api.post('/api/sms/sync-lab-catalog'),
  getSmsLabCatalogSyncStatus: () => api.get('/api/sms/sync-lab-catalog'),
  advanceSmsLabCatalogSync: () => api.post('/api/sms/sync-lab-catalog/advance'),
  syncSmsLabOrderSets: () => api.post('/api/sms/sync-lab-order-sets'),
  getSmsConfig: () => api.get('/api/sms/config'),
  setSmsDefaults: (defaults) => api.patch('/api/sms/config/defaults', defaults),
  patchSmsConfig: (patch) => api.patch('/api/sms/config', patch),
  seedSmsNorthwellCatalog: () => api.post('/api/sms/seed-northwell-catalog'),
  addSmsProviderPhone: (phone, userId) => api.post('/api/sms/config/phones', { phone, userId }),
  removeSmsProviderPhone: (phone) => api.delete('/api/sms/config/phones/' + encodeURIComponent(phone)),

  // Note Expander — server route: server/routes/noteExpander.js (admin-gated by feature flag)
  noteExpanderPatients: (q) => api.get('/api/note-expander/patients?q=' + encodeURIComponent(q)),
  noteExpanderDrafts: (directoryId) => api.get('/api/note-expander/drafts?directoryId=' + encodeURIComponent(directoryId)),
  noteExpanderExpand: (body) => api.post('/api/note-expander/expand', body),
  noteExpanderCommit: (body) => api.post('/api/note-expander/commit', body),
  // Draft Inbox (Task 5) — scan/classify all unsigned drafts, then expand-in-place.
  // expandDraft/commitDraft are the draftId-based path (no pinnedPatient): the
  // server resolves the patient + draft from draftId alone.
  noteExpanderInbox: ({ scope = 'all', q = '', offset = 0 } = {}) =>
    api.get(`/api/note-expander/inbox?scope=${encodeURIComponent(scope)}&q=${encodeURIComponent(q)}&offset=${offset}`),
  noteExpanderExpandDraft: (draftId, model, editedText) => {
    const body = { draftId };
    if (model) body.model = model;
    if (typeof editedText === 'string' && editedText.length) body.editedText = editedText;
    return api.post('/api/note-expander/expand', body);
  },
  noteExpanderCommitDraft: (body) => api.post('/api/note-expander/commit', body),
  // Patient-context header chips (Task 2) — short problem/med/allergy labels.
  noteExpanderPatientContext: (elationId) => api.get('/api/note-expander/patient-context?elationId=' + encodeURIComponent(elationId)),
  // Lazy per-draft freeform text for the pre-expand compose box → { draftId, text }.
  noteExpanderDraftText: (draftId) =>
    api.get('/api/note-expander/draft-text?draftId=' + encodeURIComponent(draftId)),
  // Feedback loop — per-user style prefs review (admins see all; providers own).
  noteExpanderPrefsList: ({ userId = '', status = '' } = {}) =>
    api.get(`/api/note-expander/prefs?userId=${encodeURIComponent(userId)}&status=${encodeURIComponent(status)}`),
  noteExpanderPrefsUpdate: (id, instructions) =>
    api.put('/api/note-expander/prefs/' + encodeURIComponent(id), { instructions }),
  noteExpanderPrefsApprove: (id) => api.post('/api/note-expander/prefs/' + encodeURIComponent(id) + '/approve', {}),
  noteExpanderPrefsRetire: (id) => api.post('/api/note-expander/prefs/' + encodeURIComponent(id) + '/retire', {}),
  noteExpanderFeedbackRecent: ({ userId = '', limit = 50 } = {}) =>
    api.get(`/api/note-expander/feedback/recent?userId=${encodeURIComponent(userId)}&limit=${limit}`),
  // Note Expander v2 — server route: server/routes/noteExpanderV2.js. Every route answers 403
  // { reason: 'engine_v1' | 'disabled' } until the practice's engine switch is v2.
  ne2Copy: () => api.get('/api/note-expander/v2/copy'),
  ne2Models: () => api.get('/api/note-expander/v2/models'),
  ne2Prefs: () => api.get('/api/note-expander/v2/me/prefs'),
  ne2SavePrefs: (body) => api.put('/api/note-expander/v2/me/prefs', body),
  // Your style (PR 8a-UI): the physician's own learned style, its range count, and its actions.
  ne2Style: () => api.get('/api/note-expander/v2/me/style'),
  ne2StyleCount: ({ from, to }) =>
    api.get('/api/note-expander/v2/me/style/count?from=' + encodeURIComponent(from) + '&to=' + encodeURIComponent(to)),
  ne2StyleBuild: ({ from, to, model }) => api.post('/api/note-expander/v2/me/style/build', model ? { from, to, model } : { from, to }),
  ne2StyleApprove: () => api.post('/api/note-expander/v2/me/style/approve', {}),
  ne2StyleDiscard: () => api.post('/api/note-expander/v2/me/style/discard', {}),
  ne2StyleStandard: () => api.post('/api/note-expander/v2/me/style/standard', {}),
  ne2StyleRestore: () => api.post('/api/note-expander/v2/me/style/restore', {}),
  ne2StyleRemovePhrase: (phraseId, target) => api.post('/api/note-expander/v2/me/style/phrases/remove', { phraseId, target }),
  ne2StyleRemoveHabit: (habitId, target) => api.post('/api/note-expander/v2/me/style/habits/remove', { habitId, target }),
  ne2StyleRevert: (version) => api.post('/api/note-expander/v2/me/style/revert', { version }),
  ne2LatestRuns: (draftIds) =>
    api.get('/api/note-expander/v2/runs/latest?draftIds=' + (draftIds || []).map((id) => encodeURIComponent(id)).join(',')),
  ne2StartRun: ({ draftId, mode, model, clientRequestId }) =>
    api.post('/api/note-expander/v2/runs', { draftId: String(draftId), mode, model, clientRequestId }),
  ne2Run: (runId) => api.get('/api/note-expander/v2/runs/' + encodeURIComponent(runId)),
  ne2Items: (revisionId) => api.get('/api/note-expander/v2/revisions/' + encodeURIComponent(revisionId) + '/items'),
  ne2Touch: (revisionId, body) => api.post('/api/note-expander/v2/revisions/' + encodeURIComponent(revisionId) + '/touch', body),
  ne2Discard: (revisionId) => api.post('/api/note-expander/v2/revisions/' + encodeURIComponent(revisionId) + '/discard', {}),
  ne2Accept: (revisionId, body) => api.post('/api/note-expander/v2/revisions/' + encodeURIComponent(revisionId) + '/accept', body),
  ne2Outbox: (revisionId) => api.get('/api/note-expander/v2/revisions/' + encodeURIComponent(revisionId) + '/outbox'),
  ne2Undo: (revisionId, items) => api.post('/api/note-expander/v2/revisions/' + encodeURIComponent(revisionId) + '/undo', { items }),
  ne2Play: (body) => api.post('/api/note-expander/v2/playground', body),
  ne2Comparison: (comparisonId) => api.get('/api/note-expander/v2/playground/' + encodeURIComponent(comparisonId)),
  ne2Promote: (runId) => api.post('/api/note-expander/v2/runs/' + encodeURIComponent(runId) + '/promote', {}),
  // The model's reasoning for one of your Playground runs (086): { state, entries }.
  ne2RunReasoning: (runId) => api.get('/api/note-expander/v2/runs/' + encodeURIComponent(runId) + '/reasoning'),

  // Batch care-plan/billing lane — server route: server/routes/batch.js.
  // enqueue submits the collected records ({ records, model? }); jobs lists this
  // tenant's jobs; results fetches one job's result rows for materialize; prune
  // tears a job down after its results are materialized.
  enqueueBatchCareplan: (records, model) =>
    api.post('/data/batch/careplanBilling/enqueue', model ? { records, model } : { records }),
  getBatchCareplanJobs: () => api.get('/data/batch/careplanBilling/jobs'),
  getBatchCareplanResults: (jobId) =>
    api.get('/data/batch/careplanBilling/' + encodeURIComponent(jobId)),
  pruneBatchCareplanJob: (jobId) =>
    api.post('/data/batch/careplanBilling/' + encodeURIComponent(jobId) + '/prune', {}),

  // Panel review dispositions — server route: server/routes/panelReview.js
  // GET takes month=YYYY-MM, POST upserts a single disposition.
  listDispositions: (month) =>
    api.get('/data/panel-review/dispositions?month=' + encodeURIComponent(month)),
  upsertDisposition: ({ patient_id, suggestion_hash, program_slot, program_family, recommended_code, status, review_month, reason, suggestion_payload }) =>
    api.post('/data/panel-review/dispositions', {
      patient_id,
      suggestion_hash,
      program_slot,
      program_family,
      recommended_code,
      status,
      review_month,
      reason: reason || '',
      suggestion_payload: suggestion_payload || null,
    }),

  // Provider e-signature (self-service — server: routes/settings.js, keyed to
  // the authenticated user; embedded on Quest home-draw forms).
  getMySignature:   ()               => api.get('/data/provider/signature'),
  setMySignature:   (format, base64) => api.post('/data/provider/signature', { format, base64 }),
  clearMySignature: ()               => api.delete('/data/provider/signature'),
  // Self-scoped consent: let the fax bot apply MY signature to incoming forms.
  setFaxAutoSign:   (enabled)        => api.post('/data/provider/fax-auto-sign', { enabled }),
};
